import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  MEDICATION_REQUESTS_KEY,
  answerMedicationRequest,
  fetchMedicationQueue,
  type AdminMedicationRequest,
} from '../api/medications'
import { EmptyState } from '../components/EmptyState'
import { FilteredEmpty } from '../components/FilteredEmpty'
import { useFilterQuery } from '../TableFilterContext'
import { RecordCard, type Fact } from '../components/RecordCard'
import { formatPesewas } from '../../lib/money'

const URGENCY_LABEL: Record<string, string> = {
  same_day: 'Same day',
  '24h': 'Within 24 hrs',
  routine: 'Routine',
}

function age(iso: string): string {
  const mins = Math.max(
    0,
    Math.round((Date.now() - new Date(iso).getTime()) / 60000),
  )
  if (mins < 60) return `${mins} min`
  const hrs = Math.round(mins / 60)
  return `${hrs} h`
}

/**
 * Pharmacy medication queue (M12-T06a, ADR-0028). hospital_admin only — every
 * active request is listed (fan-out); the pharmacy answers Available (with a
 * price) or Not available. The answer goes to the AMTC concierge, **not the
 * patient**, and is audit-logged.
 */
export function MedicationRequestsPage(): ReactNode {
  const { token } = useAuth()
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: MEDICATION_REQUESTS_KEY,
    queryFn: () => fetchMedicationQueue(token),
    enabled: Boolean(token),
  })

  // A CLIENT filter, deliberately, and `false` for "not paginated" (M25-T01).
  // `list_pharmacy_queue` carries no `LIMIT` and is drained by
  // `status = 'pending' AND expires_at > now()`, so the cards on screen already
  // ARE the whole queue and filtering them is complete. This queue must NOT get
  // a `SEARCHABLE` entry: that would make the box promise a server search that
  // never happens.
  const query = useFilterQuery(false)
  const term = query.trim().toLowerCase()
  const visible =
    data === undefined || term === ''
      ? data
      : data.filter((r) =>
          [r.reference, r.patient_name, r.drug_generic_name, r.drug_brand_name, r.area]
            .some((f) => f?.toLowerCase().includes(term)),
        )

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Medication
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-3">
          Answer yes or no with a price. Your answer goes to the AMTC concierge
          — not the patient. Every response is audit-logged.
        </p>
      </div>

      {isPending ? (
        <div
          role="status"
          aria-label="Loading medication requests"
          className="h-40 w-full max-w-3xl animate-pulse rounded-2xl bg-bg-sunken"
        />
      ) : isError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-[10px] border border-bed-bad/40 bg-bed-bad-bg px-4 py-3"
        >
          <p className="text-sm font-medium text-bed-bad">
            {error instanceof ApiError
              ? error.message
              : 'Could not load medication requests.'}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="inline-flex h-8 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft"
          >
            Retry
          </button>
        </div>
      ) : data.length === 0 ? (
        <div className="rounded-2xl border border-line-1">
          <EmptyState title="No open medication requests.">
            Requests appear here as patients ask for a drug, and expire after 24
            hours.
          </EmptyState>
        </div>
      ) : visible !== undefined && visible.length === 0 ? (
        // Filtered to nothing, which is not the same as having nothing. No
        // second line: this filter covered the whole queue.
        <div className="rounded-2xl border border-line-1">
          <FilteredEmpty query={query} />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {(visible ?? []).map((r) => (
            <QueueCard key={r.reference} request={r} />
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * The pharmacy's side of the request. **No frame draws this page** — the two
 * Medication frames both draw AMTC's curation view — so it is extended from the
 * same atoms rather than ported, as M16-T09 did for the surfaces the file
 * missed. Leaving it on the old styling because it lacks a frame would split
 * one nav item across two visual languages.
 */
function QueueCard({
  request: r,
}: {
  request: AdminMedicationRequest
}): ReactNode {
  const facts: Fact[] = [
    { label: 'NEEDED', value: URGENCY_LABEL[r.urgency] ?? r.urgency },
    { label: 'AREA', value: r.area ?? 'Not given' },
    { label: 'FORM', value: `${r.drug_form}${r.prescription_only ? ' · Rx' : ''}` },
    { label: 'WAITING', value: age(r.created_at) },
  ]
  return (
    <RecordCard
      title={`${r.drug_generic_name} ${r.drug_strength} · ${r.quantity}`}
      reference={r.reference}
      facts={facts}
      actions={<AnswerCell request={r} />}
    />
  )
}

function AnswerCell({
  request,
}: {
  request: AdminMedicationRequest
}): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const answered = request.my_available !== null
  const [editing, setEditing] = useState(!answered)
  const [cedis, setCedis] = useState('')

  const mutation = useMutation({
    mutationFn: (input: { available: boolean; price_pesewas: number | null }) =>
      answerMedicationRequest(token, request.reference, input),
    onSuccess: () => {
      setEditing(false)
      void queryClient.invalidateQueries({ queryKey: MEDICATION_REQUESTS_KEY })
    },
  })

  if (answered && !editing) {
    return (
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            request.my_available
              ? 'bg-bed-good-bg text-bed-good'
              : 'bg-bed-bad-bg text-bed-bad'
          }`}
        >
          {request.my_available
            ? `Available · ${
                request.my_price_pesewas != null
                  ? formatPesewas(request.my_price_pesewas)
                  : '—'
              }`
            : 'Not available'}
        </span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-[12px] font-medium text-accent-ink hover:underline"
        >
          Change
        </button>
      </div>
    )
  }

  const price = Math.round(Number.parseFloat(cedis) * 100)
  const priceValid = Number.isFinite(price) && price >= 0

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="sr-only" htmlFor={`price-${request.reference}`}>
        Price in GHS for {request.reference}
      </label>
      <div className="inline-flex items-center gap-1 rounded-[8px] border border-line-2 bg-bg-app px-2">
        <span className="text-[12px] text-ink-3">GHS</span>
        <input
          id={`price-${request.reference}`}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={cedis}
          onChange={(e) => setCedis(e.target.value)}
          placeholder="0.00"
          className="w-20 bg-transparent py-1 text-sm text-ink-1 focus:outline-none"
        />
      </div>
      <button
        type="button"
        disabled={!priceValid || mutation.isPending}
        onClick={() =>
          mutation.mutate({ available: true, price_pesewas: price })
        }
        className="inline-flex h-8 items-center rounded-[8px] bg-accent px-3 text-sm font-medium text-white disabled:opacity-50 hover:opacity-90"
      >
        Available
      </button>
      <button
        type="button"
        disabled={mutation.isPending}
        onClick={() =>
          mutation.mutate({ available: false, price_pesewas: null })
        }
        className="inline-flex h-8 items-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 disabled:opacity-50 hover:bg-bg-soft"
      >
        Not available
      </button>
      {mutation.isError && (
        <span role="alert" className="text-[12px] text-bed-bad">
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'Could not save.'}
        </span>
      )}
    </div>
  )
}
